import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, Type } from '@angular/core';

import { ContentComponentType } from '@ppg/core/enums';
import {
  RichTextModel,
  ContentComponent,
  ComponentImageWithColorsUsed,
  Container603010ContentModel,
  CuratedColorPalette,
  ContentComponentContentType,
} from '@ppg/core/models';
import { CuratedColorPaletteComponent } from '@ppg/shared/color/curated-color-palette';
import { ImageWithColorsUsedComponent, Container603010ContentComponent } from '@ppg/shared/components';
import { RichTextComponent } from '@ppg/shared/rich-text';

import { CONTENT_COMPONENTS_MAP } from '../data/constants/content-components-map.const';
import { INTERNAL_CONTENT_COMPONENTS_MAP } from '../data/constants/internal-content-components-map.const';

type OmitType<Component extends { type: ContentComponentType }> = Omit<Component, 'type'>;

const COMPONENTS_MAP = {
  ...INTERNAL_CONTENT_COMPONENTS_MAP,
  ...CONTENT_COMPONENTS_MAP,
} as Record<string, Type<unknown>>;

@Component({
  selector: 'ppg-shared-component-renderer',
  standalone: true,
  imports: [NgComponentOutlet],
  templateUrl: './shared-component-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedComponentRendererComponent {
  readonly contentComponent = input.required<ContentComponent>({ alias: 'component' });
  readonly isRoot = input<boolean>(false);
  readonly fullWidth = input<boolean>(false);

  readonly type = computed(() => this.contentComponent().type);
  readonly content = computed(() => this.contentComponent().content);

  readonly component = computed<Type<unknown> | null>(() => COMPONENTS_MAP[this.type() as string] ?? null);

  readonly inputs = computed<ContentComponentContentType>(() => {
    if (this.component() === RichTextComponent && this.type() !== ContentComponentType.RICH_TEXT) {
      return { content: `${this.type()} is not implemented yet` } as Pick<RichTextModel, 'content'>;
    }

    if (this.component() === RichTextComponent && this.type() === ContentComponentType.RICH_TEXT) {
      return { content: this.content(), isRoot: this.isRoot(), fullWidth: this.fullWidth() } as OmitType<RichTextModel>;
    }

    if (
      this.component() === ImageWithColorsUsedComponent &&
      this.type() === ContentComponentType.COMPONENT_IMAGE_WITH_COLORS_USED
    ) {
      return { content: this.content(), isRoot: this.isRoot() } as OmitType<ComponentImageWithColorsUsed>;
    }

    if (
      this.component() === Container603010ContentComponent &&
      this.type() === ContentComponentType.CONTAINER_603010_COLORS
    ) {
      return { content: this.content(), fullWidth: this.fullWidth() } as OmitType<Container603010ContentModel>;
    }

    if (
      this.component() === CuratedColorPaletteComponent &&
      this.type() === ContentComponentType.CURATED_COLOR_PALETTE
    ) {
      return { content: this.content(), fullWidth: this.fullWidth() } as OmitType<CuratedColorPalette>;
    }

    return { content: this.content() };
  });
}
